// project imports
// action - state management
import * as actionTypes from "../../actions/actionTypes";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import {
  CAPEX_ASSUMPTIONS_REDUCER_KEY,
  FINANCIAL_FORECAST_REDUCER_KEY,
  FREE_CASH_FLOW_MAIN_REDUCER_KEY,
  NET_WORKING_CAPITAL_ASSUMPTIONS_REDUCER_KEY,
  PROFIT_AND_LOSS_MAIN_REDUCER_KEY,
} from "..";

import {
  APP_STORE_COMMISSIONS_KEY,
  CONTENT_AND_PRODUCTION_COSTS_KEY,
  SERVER_COST_KEY,
  TOTAL_REVENUES_KEY as PNL_TOTAL_REVENUES_KEY,
  GEN_ADMIN_EXPENSES_KEY as PNL_GENERAL_ADMINISTRATIVE_EXPENSES_KEY,
  S_AND_M_EXPENSES_KEY as PNL_SALES_AND_MARKETING_EXPENSES_KEY,
} from "../profit-and-loss/mainReducer";
import { NWC_KEY } from "../assumptions/netWorkingCapitalAssumptionsReducer";
import { CAPEX_KEY as CAPEX_CAPEX_KEY } from "../assumptions/capexAssumptionsReducer";
import { defaultCurrencyFormatter } from "utils/currency-formatter";
import { hp_fp_foundersAndPartnersHeaderMonth } from "../hiring-plan/staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

const prefix = "fcf_main_";

export const TOTAL_REVENUES_KEY = prefix + "totalRevenues";
export const COST_OF_SALES_KEY = prefix + "costOfSales";
export const GROSS_PROFIT_KEY = prefix + "grossProfit";
export const GROSS_PROFIT_MARGIN_KEY = prefix + "grossProfitMargin";
export const GENERAL_ADMINISTRATIVE_EXPENSES_KEY =
  prefix + "generalAdministrativeExpenses";
export const SALES_AND_MARKETING_EXPENSES_KEY =
  prefix + "salesAndMarketingExpenses";
export const EBITDA_KEY = prefix + "ebitda";
export const EBITDA_MARGIN_KEY = prefix + "ebitdaMargin";
export const TAX_KEY = prefix + "tax";
export const OPERATING_CASH_FLOW_KEY = prefix + "operatingCashFlow";
export const NET_WORKING_CAPITAL_KEY = prefix + "netWorkingCapital";
export const CAPEX_KEY = prefix + "capex";
export const FREE_CASH_FLOW_KEY = prefix + "freeCashFlow";
export const BOP_CASH_KEY = prefix + "bopCash";
export const EOP_CASH_KEY = prefix + "eopCash";

export const freeCashFlowItems = [
  TOTAL_REVENUES_KEY,
  COST_OF_SALES_KEY,
  GROSS_PROFIT_KEY,
  GROSS_PROFIT_MARGIN_KEY,
  GENERAL_ADMINISTRATIVE_EXPENSES_KEY,
  SALES_AND_MARKETING_EXPENSES_KEY,
  EBITDA_KEY,
  EBITDA_MARGIN_KEY,
  TAX_KEY,
  OPERATING_CASH_FLOW_KEY,
  NET_WORKING_CAPITAL_KEY,
  CAPEX_KEY,
  FREE_CASH_FLOW_KEY,
  BOP_CASH_KEY,
  EOP_CASH_KEY,
];

export const initialState = {
  [TOTAL_REVENUES_KEY]: {
    title: "Revenues",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(PROFIT_AND_LOSS_MAIN_REDUCER_KEY)[
        PNL_TOTAL_REVENUES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_OF_SALES_KEY]: {
    title: "Cost of Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (companyType === GeneralCoType) {
        return 0;
      }
      const appStoreCommissions = getReducerFunc(
        PROFIT_AND_LOSS_MAIN_REDUCER_KEY
      )[APP_STORE_COMMISSIONS_KEY].calculate(
        yearKey,
        getReducerFunc,
        companyType
      );

      const contentAndProductionCosts = getReducerFunc(
        PROFIT_AND_LOSS_MAIN_REDUCER_KEY
      )[CONTENT_AND_PRODUCTION_COSTS_KEY].calculate(
        yearKey,
        getReducerFunc,
        companyType
      );

      const serverCost = getReducerFunc(PROFIT_AND_LOSS_MAIN_REDUCER_KEY)[
        SERVER_COST_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return (
        Number(appStoreCommissions) +
        Number(contentAndProductionCosts) +
        Number(serverCost)
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [GROSS_PROFIT_KEY]: {
    title: "Gross Profit",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const totalRevenues = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        TOTAL_REVENUES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const costOfSales = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        COST_OF_SALES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return Number(totalRevenues) + Number(costOfSales);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [GROSS_PROFIT_MARGIN_KEY]: {
    title: "Gross Profit Margin",
    highlight: true,
    unit: unitType.PERCENTAGE,
    unitSymbol: getUnitSymbolByUnit(unitType.PERCENTAGE),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const totalRevenues = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        TOTAL_REVENUES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const grossProfit = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        GROSS_PROFIT_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return (grossProfit / totalRevenues) * 100;
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [GENERAL_ADMINISTRATIVE_EXPENSES_KEY]: {
    title: "General Administrative Expenses",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(PROFIT_AND_LOSS_MAIN_REDUCER_KEY)[
        PNL_GENERAL_ADMINISTRATIVE_EXPENSES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SALES_AND_MARKETING_EXPENSES_KEY]: {
    title: "Sales & Marketing Expenses",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(PROFIT_AND_LOSS_MAIN_REDUCER_KEY)[
        PNL_SALES_AND_MARKETING_EXPENSES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [EBITDA_KEY]: {
    title: "EBITDA",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const grossProfit = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        GROSS_PROFIT_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const generalAdministrativeExpenses = getReducerFunc(
        FREE_CASH_FLOW_MAIN_REDUCER_KEY
      )[GENERAL_ADMINISTRATIVE_EXPENSES_KEY].calculate(
        yearKey,
        getReducerFunc,
        companyType
      );

      const salesAndMarketingExpenses = getReducerFunc(
        FREE_CASH_FLOW_MAIN_REDUCER_KEY
      )[SALES_AND_MARKETING_EXPENSES_KEY].calculate(
        yearKey,
        getReducerFunc,
        companyType
      );

      return (
        Number(grossProfit) +
        Number(generalAdministrativeExpenses) +
        Number(salesAndMarketingExpenses)
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,

    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [EBITDA_MARGIN_KEY]: {
    title: "EBITDA Margin",
    highlight: true,
    unit: unitType.PERCENTAGE,
    unitSymbol: getUnitSymbolByUnit(unitType.PERCENTAGE),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const ebitda = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        EBITDA_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const totalRevenues = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        TOTAL_REVENUES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return (ebitda / totalRevenues) * 100;
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TAX_KEY]: {
    title: "TAX",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const ebitda = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        EBITDA_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const corporateTax = getReducerFunc(FINANCIAL_FORECAST_REDUCER_KEY)[
        "generalAssumptions"
      ]["corporateTax"];

      const result = ebitda < 0 ? 0 : ((ebitda * corporateTax) / 100) * -1;

      return result.toFixed(0);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [OPERATING_CASH_FLOW_KEY]: {
    title: "Operating Cash Flow",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const ebitda = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        EBITDA_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const tax = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        TAX_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return Number(ebitda) + Number(tax);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [NET_WORKING_CAPITAL_KEY]: {
    title: "Net Working Capital",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const totalRevenues = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        TOTAL_REVENUES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const nwc = getReducerFunc(NET_WORKING_CAPITAL_ASSUMPTIONS_REDUCER_KEY)[
        NWC_KEY
      ]["years"][yearKey];

      return Number((totalRevenues * nwc) / 100).toFixed(0);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CAPEX_KEY]: {
    title: "CAPEX",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const totalRevenues = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        TOTAL_REVENUES_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const nwc = getReducerFunc(CAPEX_ASSUMPTIONS_REDUCER_KEY)[
        CAPEX_CAPEX_KEY
      ]["years"][yearKey];

      const result = (totalRevenues * nwc) / 100;

      return Number(result === 0 ? 0 : result * -1).toFixed(0);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [FREE_CASH_FLOW_KEY]: {
    title: "Free Cash Flow / Net Cash Change",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    formatter: defaultCurrencyFormatter,
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const operatingCashFlow = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        OPERATING_CASH_FLOW_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const netWorkingCapital = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        NET_WORKING_CAPITAL_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const capex = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        CAPEX_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return (
        Number(operatingCashFlow) + Number(netWorkingCapital) + Number(capex)
      );
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [BOP_CASH_KEY]: {
    title: "BoP Cash",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    formatter: defaultCurrencyFormatter,
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let eopCash = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        EOP_CASH_KEY
      ]["initialPeriod"];

      if (yearKey > 1) {
        eopCash = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
          EOP_CASH_KEY
        ].calculate(yearKey - 1, getReducerFunc, companyType);
      }

      return Number(eopCash).toFixed(0);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [EOP_CASH_KEY]: {
    title: "EoP Cash",
    highlight: true,
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    formatter: defaultCurrencyFormatter,
    initialPeriod: 10000,
    initialPeriodMonth: 10000,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      // useEffect for initialPeriod
      const freeCashFlow = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        FREE_CASH_FLOW_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const bopCash = getReducerFunc(FREE_CASH_FLOW_MAIN_REDUCER_KEY)[
        BOP_CASH_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      return Number(freeCashFlow) + Number(bopCash);
    },
    months: hp_fp_foundersAndPartnersHeaderMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const freeCashFlowMainReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_FREE_CASH_FLOW_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_FREE_CASH_FLOW_BY_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_FREE_CASH_FLOW_INITIAL_PERIOD:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          initialPeriod: action.initialPeriod,
        },
      };
    case actionTypes.SET_FREE_CASH_FLOW_INITIAL_PERIOD_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          initialPeriodMonth: action.initialPeriod,
        },
      };
    case actionTypes.REPLACE_FREE_CASH_FLOW_BY_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_FREE_CASH_FLOW_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default freeCashFlowMainReducer;
